import restClient from 'config/httpClient';
import { all, call, delay, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { redirect } from 'utils/utils';
import { INTERVENTION_UPDATE_MODAL_TYPES } from 'works/components/Content/components/Common/modalUtils';

import { PREVIEW_URL } from 'constants/Global';

import { putAndWait } from 'modules/app/sagas';
import { fetchEntity } from 'modules/entity/actions';
import { simpleRequestsLoader } from 'modules/app/utils';
import { FETCH_ENTITY_SUCCESS } from 'modules/entity/actionTypes';

import { getContact, getEntity } from 'modules/entity/selectors';
import { createNotification } from 'modules/notifications/actions';
import { waitForSignatureFlow } from 'modules/entity/sagas/signatureModal';
import { openAppointmentBookingModal, openInterventionUpdateModal, selectTab } from 'modules/works/actions';
import * as actionTypes from '../actionTypes';
import { CHECKLIST_GROUP_STATUS, PAYMENT_STATUS } from '../constants';

import paymentSagas, { setSignatureFlagBeforeRedirect } from './payment';
import mediaSagas from './media';

import * as payActions from '../actions/payment';
import * as worksActions from '../actions/works';
import * as generalActions from '../actions/general';

import { getMostRecentPayment } from '../utils';
import { getMainContent } from '../selectors/general';
import { isPaymentPending as isPaymentPendingSelector } from '../selectors/payment';
import { isReminder } from '../selectors';

const RETRY_PAYMENT_DELAY = 5000;

const SELECT_TAB_ACTIONS = [
  FETCH_ENTITY_SUCCESS,
  actionTypes.ACCEPT_AND_PAY_WORKS_SUCCESS,
  actionTypes.ANSWER_WORKS_SUCCESS,
];

export function* afterFetchIntervention({ payload }) {
  const { getPaymentsUrl: url } = payload;
  if (url && url !== PREVIEW_URL) {
    let isPendingPayment = yield select(isPaymentPendingSelector);
    while (isPendingPayment) {
      yield delay(RETRY_PAYMENT_DELAY);
      yield putAndWait(payActions.fetchPayments(url));
      isPendingPayment = yield select(isPaymentPendingSelector);
    }
  }
}

/**
 * @param {{policies: Array<import('../types').Policy>}} payload
 */
export function* acceptPolicies(payload = []) {
  const { acceptPoliciesUrl } = yield select(getEntity);
  if (acceptPoliciesUrl && Object.values(payload).length > 0) {
    yield call(restClient.request, {
      method: 'POST',
      url: acceptPoliciesUrl,
      data: { policies: payload },
    });
  }
}

export function* answeredReminderWorksWorker({ payload }) {
  const { request } = payload;
  const isAReminder = yield select(isReminder);

  const { error } = yield take([actionTypes.ANSWER_WORKS_SUCCESS, actionTypes.ANSWER_WORKS_FAILURE]);

  if (!error) {
    const hasNewlyAcceptedAdvice = isAReminder && request.data?.advices?.some(
      (advice) => advice.status === CHECKLIST_GROUP_STATUS.ACCEPTED
    );
    const { appointmentLink, tel } = yield select(getContact);
    const showModal = hasNewlyAcceptedAdvice && (appointmentLink || tel);
    if (showModal) {
      yield call(waitForSignatureFlow);
      yield put(openAppointmentBookingModal());
    }
  }
}

/**
 * @param {import('../types').AnswerWorksAction} action
 */
export function* answerWorks({ payload, meta = {} }) {
  const { request, policies } = payload;
  const { target } = meta;
  const isOffer = target === 'offer';

  try {
    yield call(acceptPolicies, policies);
    const { data: intervention } = yield call(restClient.request, request);
    yield put(worksActions.answerWorksSuccess(intervention));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'web.answer',
          defaultMessage: 'Your reply was sent.'
        }
      })
    );
  } catch (error) {
    yield put(worksActions.answerWorksFailure(error));
    if (error.response?.status === 409) {
      yield put(openInterventionUpdateModal(
        { type: INTERVENTION_UPDATE_MODAL_TYPES.ADVICE_VERSION }
      ));
    } else {
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: isOffer ? 'web.offer.accept.failure' : 'web.accepted.error',
            defaultMessage: isOffer ? 'An error has occurred while accepting the offer' : 'Works couldn’t be accepted.'
          }
        })
      );
    }
  }
}

/** @param {ReturnType<import('../types').AcceptAndPayWorksAction>} action */
export function* acceptWorksAndPay({ payload }) {
  const { request, policies } = payload;

  try {
    yield call(acceptPolicies, policies);
    const { data: payment } = yield call(restClient.request, request);
    yield put(worksActions.acceptAndPayWorksSuccess(payment));
    yield call(setSignatureFlagBeforeRedirect);
    yield call(redirect, payment.redirectUrl);
  } catch (error) {
    yield put(worksActions.acceptAndPayWorksFailure(error));
    if (error.response?.status === 409) {
      yield put(openInterventionUpdateModal(
        { type: INTERVENTION_UPDATE_MODAL_TYPES.ADVICE_VERSION }
      ));
    } else {
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: 'web.accepted.error',
            defaultMessage: 'Works couldn’t be accepted.',
          },
        })
      );
    }
  }
}

export function* sendContactForm({ payload }) {
  const { request } = payload;
  try {
    const { data: intervention } = yield call(restClient.request, request);
    yield put(generalActions.sendContactFormSuccess(intervention));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'web.contact-form.success',
          defaultMessage: 'Your request has been sent',
        },
      })
    );
  } catch (error) {
    yield put(generalActions.sendContactFormFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'web.contact-form.failure',
          defaultMessage: 'An error has occurred while sending your request',
        },
      })
    );
  }
}

export function* refreshLayout() {
  const tab = yield select(getMainContent);
  yield put(selectTab(tab));
}

export function* afterFetchPayments({ payload: payments }) {
  const latestPayment = getMostRecentPayment(payments);
  if (latestPayment && latestPayment.status !== PAYMENT_STATUS.PENDING) {
    yield put(fetchEntity());
  }
}

const requestsConfig = {
  [actionTypes.SEND_OPINION]: {
    notifications: {
      success: {
        id: 'web.opinion.success',
        defaultMessage: 'Your opinion has been sent.',
      },
      failure: {
        id: 'web.opinion.failure',
        defaultMessage: 'Your opinion couldn’t be send.',
      },
    },
  },
  [actionTypes.CALL_ME_BACK]: {
    notifications: {
      success: {
        id: 'email.customer_answered_in_sales.opinion.call_me_back.success',
        defaultMessage: 'Your contact request has been sent.',
      },
      failure: {
        id: 'email.customer_answered_in_sales.opinion.call_me_back.failure',
        defaultMessage: 'Your contact request cannot be sent.',
      },
    },
  },
  [actionTypes.FETCH_PAYMENTS]: {
    notifications: {
      /**
       * @param {import('../types').FetchPaymentsSuccessAction} action
       * @returns {import('react-intl').MessageDescriptor}
       */
      success: ({ payload: payments }) => {
        const latestPayment = getMostRecentPayment(payments);
        if (latestPayment) {
          if (latestPayment.status === PAYMENT_STATUS.SUCCESS) {
            return { id: 'web.payments.success', defaultMessage: 'Payment succeeded' };
          }
          if (latestPayment.status === PAYMENT_STATUS.FAILED) {
            return { id: 'web.payments.failure', defaultMessage: 'Payment failed' };
          }
        }
        return undefined;
      },
      failure: {
        id: 'web.payments.fetchError',
        defaultMessage: 'An error occurred while getting payments information',
      },
    },
  },
};

export default function* interventionSagas() {
  yield fork(paymentSagas);
  yield fork(mediaSagas);
  yield takeLatest(actionTypes.ANSWER_WORKS, answerWorks);
  yield takeLatest(actionTypes.ANSWER_WORKS, answeredReminderWorksWorker);
  yield takeLatest(actionTypes.ACCEPT_AND_PAY_WORKS, acceptWorksAndPay);
  yield takeLatest(actionTypes.SEND_CONTACT_FORM, sendContactForm);
  yield takeLatest(SELECT_TAB_ACTIONS, refreshLayout);
  yield takeLatest(actionTypes.FETCH_PAYMENTS_SUCCESS, afterFetchPayments);
  yield takeLatest(FETCH_ENTITY_SUCCESS, afterFetchIntervention);
  yield all(simpleRequestsLoader(requestsConfig));
}
